/**
 * KCC's PRIORITY SCALE, AS A DISPLAY ADAPTER (decision UD-9, on top of S1-D1).
 *
 * The platform stores STR priority on the internal scale: an integer 0..99, **99 = most
 * important**, the scale of `priority_policy.begin_priority`. KCC's people know the SAP `TPRIO`
 * scale: an integer 0..99, **0 = most important** (real traffic uses 0, 2, 4, 6, 8, 10, 11). The
 * mapping is `99 - x`, its own inverse, exactly `projects/api2/app/tenants/kcc/priority.py`
 * (`kc_priority_to_internal` / `internal_priority_to_kc`) and the legacy
 * `pai_py.tenant.kcc.str_priority`. THIS FILE IS THE ONLY PLACE IN THE SPA WHERE THAT ARITHMETIC
 * LIVES: core asks the enabled module for a `PriorityDisplay` and never learns the `99`.
 *
 * One deliberate difference from the legacy functions, and the same one the API makes: **nothing
 * here clamps.** The legacy code pulled an out-of-range value into 0..99, which turns a malformed
 * priority into a plausible one. `toDisplay` / `toStored` do the plain arithmetic, so a bad value
 * comes back bad, and `format` prints it as itself with a flag. After tenant `V10` a stored
 * priority outside 0..99 cannot exist (a CHECK says so), so the flag is a tripwire, not a
 * feature; a request that arrives out of range is rejected by the server (`422`), not here.
 *
 * Display only. What is stored, sent and sorted by the server stays on the internal scale. A CSV
 * export is generated server-side: the API's own KCC presenter (`projects/api2/app/tenants/kcc/
 * exports.py`) shows the priority-policy export on the KC scale as `kc_priority_to`, beside
 * `begin_priority_internal`; the solver-input package stays internal for every tenant.
 */
import type { PriorityDisplay } from "../../domain/tenantModule";

const KC_PRIORITY_MIN = 0;
const KC_PRIORITY_MAX = 99;

/** The pivot of `99 - x`; also the top of the internal scale. */
const PIVOT = KC_PRIORITY_MAX;

/** An integer inside 0..99 — valid on BOTH scales, since both span the same 100 values. */
export function isPriorityInScale(value: number): boolean {
  return Number.isInteger(value) && value >= KC_PRIORITY_MIN && value <= KC_PRIORITY_MAX;
}

/** Internal (99 = most important) -> KC (0 = most important). Plain arithmetic, no clamp. */
export function internalToKcPriority(internal: number): number {
  return PIVOT - internal;
}

/** KC -> internal. The same function: `99 - (99 - x) === x`. Kept apart so a call site says why. */
export function kcToInternalPriority(kc: number): number {
  return PIVOT - kc;
}

/** One stored priority for a KCC reader: the KC number, or the stored one flagged when it is out of range. */
export function formatKcPriority(internal: number): string {
  if (typeof internal !== "number") return "—";
  if (!isPriorityInScale(internal)) return `${internal} (outside 0–99)`;
  return String(internalToKcPriority(internal));
}

export const kccPriorityDisplay: PriorityDisplay = {
  adapted: true,
  label: "KC priority",
  note: "0 = most important",
  toDisplay: internalToKcPriority,
  toStored: kcToInternalPriority,
  format: formatKcPriority,
};
